/**
 * Client-side derived column evaluation — mirrors backend derivedColumnEvaluator.
 */

function walk(obj, parts) {
  let cur = obj;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
}

function parsePath(path) {
  return String(path || '')
    .replace(/\[(\d+)\]/g, '.$1')
    .replace(/\[["']([^"']+)["']\]/g, '.$1')
    .split('.')
    .filter(Boolean);
}

export function getPath(obj, path) {
  if (obj == null || !path) return undefined;
  return walk(obj, parsePath(path));
}

export function displayDerivedValue(v) {
  if (v == null || v === '') return '';
  if (typeof v === 'object') {
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  return String(v);
}

export function rowScopeFromGridRow(row, inputColumns) {
  const input = {};
  for (const c of inputColumns) {
    if (row[c] != null) input[c] = row[c];
  }
  let metadata = {};
  const mstr = row.__metadata != null ? String(row.__metadata).trim() : '';
  if (mstr) {
    try {
      const parsed = JSON.parse(mstr);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) metadata = parsed;
    } catch { metadata = { note: mstr }; }
  }
  const expected = row.__expected ?? '';
  return { input, metadata, expected_output: expected, expected };
}

function extractParenArg(src, openIdx) {
  let depth = 0;
  let start = -1;
  for (let i = openIdx; i < src.length; i += 1) {
    const c = src[i];
    if (c === '(') {
      if (depth === 0) start = i + 1;
      depth += 1;
    } else if (c === ')') {
      depth -= 1;
      if (depth === 0) return { arg: src.slice(start, i).trim(), end: i + 1 };
    }
  }
  throw new Error('Unclosed parenthesis');
}

function resolveIdentPath(scope, path) {
  const direct = getPath(scope, path);
  if (direct !== undefined) return direct;
  return getPath(scope.input, path);
}

function evalExprString(expr, scope) {
  const trimmed = String(expr || '').trim();
  if (!trimmed) return '';

  const upper = trimmed.toUpperCase();
  if (upper.startsWith('LENGTH(')) {
    const { arg, end } = extractParenArg(trimmed, trimmed.indexOf('('));
    if (trimmed.slice(end).trim()) throw new Error('Unexpected tokens after LENGTH()');
    return String(evalExprString(arg, scope) ?? '').length;
  }
  if (upper.startsWith('UPPER(')) {
    const { arg, end } = extractParenArg(trimmed, trimmed.indexOf('('));
    if (trimmed.slice(end).trim()) throw new Error('Unexpected tokens after UPPER()');
    return String(evalExprString(arg, scope) ?? '').toUpperCase();
  }
  if (upper.startsWith('LOWER(')) {
    const { arg, end } = extractParenArg(trimmed, trimmed.indexOf('('));
    if (trimmed.slice(end).trim()) throw new Error('Unexpected tokens after LOWER()');
    return String(evalExprString(arg, scope) ?? '').toLowerCase();
  }

  if (trimmed.includes('||')) {
    return trimmed.split('||').map((part) => displayDerivedValue(evalExprString(part.trim(), scope))).join('');
  }

  if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
    return trimmed.slice(1, -1);
  }

  return resolveIdentPath(scope, trimmed);
}

export function evaluateDerivedColumn(def, row, inputColumns) {
  if (!def || typeof def !== 'object') return '';
  const scope = rowScopeFromGridRow(row, inputColumns);
  try {
    if (def.mode === 'path' && def.path) {
      return displayDerivedValue(getPath(scope, def.path));
    }
    if (def.mode === 'expr' && def.expr) {
      return displayDerivedValue(evalExprString(def.expr, scope));
    }
    return '';
  } catch {
    return '';
  }
}

export function normalizeDerivedColumns(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((d) => d && typeof d === 'object' && d.name && (d.mode === 'path' || d.mode === 'expr'))
    .map((d, i) => ({
      id: d.id ? String(d.id) : `derived_${i}`,
      name: String(d.name).slice(0, 60),
      mode: d.mode,
      path: d.mode === 'path' ? String(d.path || '') : undefined,
      expr: d.mode === 'expr' ? String(d.expr || '') : undefined,
    }));
}

export function discoverFieldSchema(rows, inputColumns = []) {
  const inputKeys = new Set(inputColumns);
  const metaKeys = new Set();
  for (const row of rows || []) {
    for (const k of inputColumns) {
      if (row[k] != null) inputKeys.add(k);
    }
    const mstr = row.__metadata != null ? String(row.__metadata).trim() : '';
    if (mstr) {
      try {
        const parsed = JSON.parse(mstr);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          Object.keys(parsed).forEach((k) => metaKeys.add(k));
        }
      } catch { /* ignore */ }
    }
  }
  return { input: [...inputKeys], metadata: [...metaKeys], has_expected: true };
}

export function newDerivedId() {
  return `dc_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}
